import { useState, useEffect, FormEvent } from 'react';
import { getSettings, updateSettings, resetThemeColorsFromBadge, changePassword, getBadgeUploadUrl, updateBadge, Settings as SettingsType, getStripeConnectStatus, getStripeDashboardLink, StripeConnectStatus, getSubscriptionStatus, createCustomerPortalSession, SubscriptionStatus, getAdminUsers, inviteAdminUser, removeAdminUser, updateAdminUser, AdminUser, AdminUsersResponse, exportClubData, deleteAccount } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { ClubPaymentsScreens } from '../components/StripeConnectScreens';
import './Settings.css';

const PUBLIC_API_URL = 'https://api.grassrootsfc.co.uk';

async function fetchClubBadgeUrl(clubId: string): Promise<string> {
  const response = await fetch(`${PUBLIC_API_URL}/api/v1/clubs/${clubId}`);
  if (!response.ok) return '';
  const data = await response.json();
  return (data?.badgeUrl as string) || '';
}

function Settings() {
  const { clubId, plan, email: currentUserEmail, isOwner: isOwnerFromJwt, forcePasswordChange, clearForcePasswordChange, features, logout } = useAuth();

  // ── Danger zone (export + self-service deletion) ──
  const [isExporting, setIsExporting] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [dangerError, setDangerError] = useState('');

  const handleExportData = async () => {
    setIsExporting(true);
    setDangerError('');
    try {
      const bundle = await exportClubData();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clubId || 'club'}-grassrootsfc-export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : 'Export failed — try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeletingAccount(true);
    setDangerError('');
    try {
      await deleteAccount();
      // The club (and this login) no longer exist — end the session.
      logout();
      window.location.href = '/';
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : 'Deletion failed — contact hello@grassrootsfc.co.uk.');
      setIsDeletingAccount(false);
    }
  };
  // Preview mode — when the operator flag features.preview is on AND
  // the club is paid, every Save button on this page routes to a draft
  // rather than going live. The amber banner at the top of the page
  // (below the page-header) makes that obvious so the operator isn't
  // surprised that Save doesn't immediately change the live site.
  const previewMode = features?.preview === true && (plan === 'club' || plan === 'pro');
  const saveButtonLabel = (verb: string) => previewMode ? `${verb} to draft` : verb;
  const isFree = plan === 'free';

  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    contactPhone: '',
    facebook: '',
    twitter: '',
    instagram: '',
    maintenanceMode: false,
    teamCardCoaches: 'all' as 'all' | 'lead',
    gaTrackingId: '',
    // null = Classic (default look, no override).
    // 'matchday' = Matchday theme (dark/parallax).
    // 'newsfeed' = Newsfeed theme (Cambridge-style light).
    // 'victory' = Victory theme (club-colour header, news first, light).
    themeName: null as 'matchday' | 'newsfeed' | 'victory' | null,
  });

  // Theme picker — Pro tier only
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeError, setThemeError] = useState('');
  const [themeSuccess, setThemeSuccess] = useState('');

  // Brand colours — free for all tiers. Admin picks Primary + Secondary;
  // server derives the 4 light/dark variants via HSL adjustment.
  const [primaryColor, setPrimaryColor] = useState('#22c55e');
  const [secondaryColor, setSecondaryColor] = useState('#1a1a1a');
  const [hasBadge, setHasBadge] = useState(false);
  const [coloursSaving, setColoursSaving] = useState(false);
  const [coloursResetting, setColoursResetting] = useState(false);
  const [coloursError, setColoursError] = useState('');
  const [coloursSuccess, setColoursSuccess] = useState('');

  // Badge state
  const [badgeUrl, setBadgeUrl] = useState('');
  const [isUploadingBadge, setIsUploadingBadge] = useState(false);
  const [badgeError, setBadgeError] = useState('');
  const [badgeSuccess, setBadgeSuccess] = useState('');

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [stripeError, setStripeError] = useState('');
  const [stripeMessage, setStripeMessage] = useState('');

  // Subscription state
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isManagingSub, setIsManagingSub] = useState(false);

  // Admin users state
  const [adminUsers, setAdminUsers] = useState<AdminUsersResponse | null>(null);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
    if (clubId) {
      fetchClubBadgeUrl(clubId).then((url) => {
        if (url) setBadgeUrl(url);
      });
    }
    // Load Stripe Connect status for all plans (shop is available on all plans)
    loadStripeStatus();
    // Load subscription status
    loadSubscriptionStatus();
    // Load admin users
    loadAdminUsers();
    // Handle Stripe redirect URL params
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'success') {
      setStripeMessage('Stripe account connected! Checking status...');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload status after a short delay (Stripe may take a moment)
      setTimeout(() => loadStripeStatus(), 1500);
    } else if (stripeParam === 'refresh') {
      setStripeError('Stripe onboarding was not completed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // If redirected here to force password change, scroll to the password section
    const changeParam = params.get('changePassword');
    if (changeParam === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => {
        const el = document.getElementById('change-password-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [clubId]);

  const loadAdminUsers = async () => {
    setIsLoadingAdmins(true);
    try {
      const data = await getAdminUsers();
      setAdminUsers(data);
    } catch (err) {
      console.error('Failed to load admin users:', err);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  const handleInviteAdmin = async (e: FormEvent) => {
    e.preventDefault();
    setAdminError('');
    setAdminSuccess('');
    setIsInviting(true);

    try {
      await inviteAdminUser(inviteEmail, inviteName);
      setAdminSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteName('');
      setShowInviteModal(false);
      await loadAdminUsers();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const [togglingEmail, setTogglingEmail] = useState<string | null>(null);
  const handleToggleEnquiries = async (admin: AdminUser) => {
    setAdminError('');
    setAdminSuccess('');
    setTogglingEmail(admin.email);
    try {
      await updateAdminUser(admin.email, { notifyInquiries: admin.notifyInquiries === false });
      await loadAdminUsers();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Couldn't change that. Try again.");
    } finally {
      setTogglingEmail(null);
    }
  };

  const handleRemoveAdmin = async (emailToRemove: string) => {
    if (!window.confirm(`Remove ${emailToRemove} from this club's admin team?`)) return;
    setAdminError('');
    setAdminSuccess('');
    setRemovingEmail(emailToRemove);

    try {
      await removeAdminUser(emailToRemove);
      setAdminSuccess(`${emailToRemove} has been removed`);
      await loadAdminUsers();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to remove admin');
    } finally {
      setRemovingEmail(null);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const status = await getStripeConnectStatus();
      setStripeStatus(status);
      if (status.connected && status.chargesEnabled) {
        setStripeMessage('');
      }
    } catch (err) {
      console.error('Failed to load Stripe status:', err);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const status = await getSubscriptionStatus();
      setSubscription(status);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
    }
  };

  const handleManageSubscription = async () => {
    setIsManagingSub(true);
    try {
      const { url } = await createCustomerPortalSession();
      window.location.href = url;
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to open subscription management');
      setIsManagingSub(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    try {
      const result = await getStripeDashboardLink();
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to open Stripe dashboard');
    }
  };

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getSettings();
      setSettings(data);
      setFormData({
        siteName: data.siteName || '',
        siteDescription: data.siteDescription || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone || '',
        facebook: data.socialMedia?.facebook || '',
        twitter: data.socialMedia?.twitter || '',
        instagram: data.socialMedia?.instagram || '',
        maintenanceMode: data.maintenanceMode || false,
        teamCardCoaches: data.teamCardCoaches === 'lead' ? 'lead' : 'all',
        gaTrackingId: data.gaTrackingId || '',
        themeName: (data.themeName === 'matchday' || data.themeName === 'newsfeed' || data.themeName === 'victory') ? data.themeName : null,
      });
      if (data.themeColors) {
        setPrimaryColor(data.themeColors.primaryColor);
        setSecondaryColor(data.themeColors.secondaryColor);
      }
      setHasBadge(!!data.hasBadge);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Brand colours handlers ───────────────────────────────────────
  const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

  const handleSaveColours = async () => {
    setColoursError('');
    setColoursSuccess('');
    if (!HEX_RE.test(primaryColor) || !HEX_RE.test(secondaryColor)) {
      setColoursError('Both colours must be valid hex codes (e.g. #1256aa).');
      return;
    }
    setColoursSaving(true);
    try {
      await updateSettings({ themeColors: { primaryColor, secondaryColor } });
      setColoursSuccess(previewMode
        ? `Saved to draft. Refresh ${clubId}-preview.grassrootsfc.co.uk to see the new colours. Use "Publish all" in the amber bar to push them live.`
        : 'Saved. Your website + emails will reflect the new colours within a minute (CDN cache).');
      setTimeout(() => setColoursSuccess(''), 6000);
    } catch (err) {
      setColoursError(err instanceof Error ? err.message : 'Failed to save colours');
    } finally {
      setColoursSaving(false);
    }
  };

  const handleResetColoursToBadge = async () => {
    if (!hasBadge) {
      setColoursError('Upload a badge first — we extract colours from your badge image.');
      return;
    }
    if (!confirm('Reset to the colours auto-extracted from your badge? Any custom colours you set will be lost.')) {
      return;
    }
    setColoursError('');
    setColoursSuccess('');
    setColoursResetting(true);
    try {
      const updated = await resetThemeColorsFromBadge();
      if (updated.themeColors) {
        setPrimaryColor(updated.themeColors.primaryColor);
        setSecondaryColor(updated.themeColors.secondaryColor);
      }
      setColoursSuccess('Reset to badge colours. Your website + emails will update within a minute.');
      setTimeout(() => setColoursSuccess(''), 6000);
    } catch (err) {
      setColoursError(err instanceof Error ? err.message : 'Failed to reset colours');
    } finally {
      setColoursResetting(false);
    }
  };

  // WCAG contrast ratio against white — drives the readability hint
  // shown next to each picker. Mirrors the website's contrastClamp.ts
  // algorithm so the UX warning matches the runtime auto-fix.
  const contrastVsWhite = (hex: string): number => {
    if (!HEX_RE.test(hex)) return 0;
    let h = hex.replace(/^#/, '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const ch = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const lum = 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
    return (1.0 + 0.05) / (lum + 0.05);
  };
  const contrastBadge = (hex: string): { label: string; color: string } => {
    const r = contrastVsWhite(hex);
    if (r >= 7) return { label: 'Excellent on white (AAA)', color: '#15803d' };
    if (r >= 4.5) return { label: 'Good on white (AA)', color: '#15803d' };
    if (r >= 3) return { label: 'Borderline on white — auto-darkened for text', color: '#92400e' };
    return { label: 'Very pale on white — auto-darkened for text', color: '#92400e' };
  };

  const handleBadgeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setBadgeError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setBadgeError('Image must be less than 5MB');
      return;
    }

    setBadgeError('');
    setBadgeSuccess('');
    setIsUploadingBadge(true);

    try {
      // 1. Get presigned URL for badges bucket
      const { uploadUrl, publicUrl } = await getBadgeUploadUrl(file.name, file.type);

      // 2. Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      // 3. Update badge (runs Rekognition + updates club record)
      await updateBadge(publicUrl);

      // 4. Update local state
      setBadgeUrl(publicUrl);
      setBadgeSuccess('Badge updated successfully! Theme colours will be extracted automatically.');
    } catch (err) {
      setBadgeError(err instanceof Error ? err.message : 'Failed to upload badge');
    } finally {
      setIsUploadingBadge(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const [sectionSaving, setSectionSaving] = useState('');
  const [sectionNote, setSectionNote] = useState<{ key: string; ok: boolean; text: string } | null>(null);

  // Each group saves only its own fields.
  const saveSection = async (key: string, fields: Parameters<typeof updateSettings>[0], toDraft = true) => {
    setSectionNote(null);
    setSectionSaving(key);
    try {
      await updateSettings(fields);
      setSectionNote({ key, ok: true, text: previewMode && toDraft ? 'Saved to your draft' : 'Saved' });
      await loadSettings();
    } catch (err) {
      setSectionNote({ key, ok: false, text: err instanceof Error ? err.message : "Couldn't save. Try again." });
    } finally {
      setSectionSaving('');
    }
  };
  const note = (key: string) => (sectionNote?.key === key
    ? <span className={`st-note${sectionNote.ok ? '' : ' is-error'}`}>{sectionNote.text}</span> : null);

  // Theme is saved on selection (no need to click main Save button) —
  // simpler UX since it's a single one-of-N choice with instant feedback.
  // `null` represents Classic = no theme override → lambda REMOVEs the
  // field from DDB and the website falls back to the default look.
  const handleSaveTheme = async (themeName: 'matchday' | 'newsfeed' | 'victory' | null) => {
    setThemeError('');
    setThemeSuccess('');
    setThemeSaving(true);
    try {
      await updateSettings({ themeName });
      setFormData((prev) => ({ ...prev, themeName }));
      setThemeSuccess(previewMode ? 'Theme saved to your draft.' : 'Theme saved. Your website shows it on the next page load.');
      setTimeout(() => setThemeSuccess(''), 4000);
    } catch (err) {
      setThemeError(err instanceof Error ? err.message : 'Failed to save theme');
    } finally {
      setThemeSaving(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // If this was a forced password change, clear the flag
      if (forcePasswordChange) {
        clearForcePasswordChange();
      }
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return <div className="loading">Loading settings...</div>;
  }

  const adminLimit = adminUsers?.limit ?? 1;
  const adminCount = adminUsers?.count ?? 0;
  const atAdminLimit = adminCount >= adminLimit;

  // Derive isOwner from JWT role OR from admin list (handles legacy sole-admin clubs)
  const myAdminRecord = adminUsers?.admins.find(a => a.email === currentUserEmail);
  const isOwner = isOwnerFromJwt || myAdminRecord?.role === 'owner';

  const groups = [
    { id: 'club', label: 'Your club' },
    { id: 'contact', label: 'Contact' },
    { id: 'billing', label: 'Plan and billing' },
    { id: 'people', label: 'People and security' },
    { id: 'danger', label: 'Danger zone' },
  ];
  // Only club name, contact details and colours go to the draft in preview mode.
  const savingLabel = (key: string, verb: string, toDraft = true) =>
    (sectionSaving === key ? 'Saving…' : toDraft ? saveButtonLabel(verb) : verb);

  return (
    <div className="settings-page st-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Your club, your plan, and who can manage this dashboard.</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {previewMode && (
        <div className="settings-preview-notice">
          <strong>Preview mode is on.</strong> Your club name, colours and theme save to your draft.
          Publish them from the amber bar at the top.
        </div>
      )}

      {forcePasswordChange && (
        <div className="admin-force-pw-banner">
          <strong>Action required:</strong> change your temporary password under People and security.
        </div>
      )}

      <div className="st-layout">
        <div className="st-main">
          {/* Your club */}
          <section id="club" className="st-group">
            <header className="st-group-head">
              <h2>Your club</h2>
              <p>How your club looks on your website.</p>
            </header>

            <div className="st-block">
              <div className="st-badge-row">
                <div className="st-badge-frame">
                  {badgeUrl ? <img src={badgeUrl} alt="Current badge" /> : <span>No badge</span>}
                </div>
                <div>
                  <label className="btn btn-secondary" style={{ cursor: isUploadingBadge ? 'wait' : 'pointer' }}>
                    {isUploadingBadge ? 'Uploading…' : badgeUrl ? 'Change badge' : 'Upload badge'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} disabled={isUploadingBadge} onChange={handleBadgeUpload} />
                  </label>
                  <p className="form-hint">PNG or JPG up to 5MB. Saves as soon as it uploads, and we check it first.</p>
                </div>
              </div>
              {badgeError && <div className="error-message">{badgeError}</div>}
              {badgeSuccess && <div className="success-message">{badgeSuccess}</div>}
            </div>

            <div className="st-block">
              <div className="st-fields">
                <div className="form-group">
                  <label htmlFor="siteName" className="form-label">Club name</label>
                  <input id="siteName" type="text" className="form-input" value={formData.siteName}
                    onChange={(e) => setFormData({ ...formData, siteName: e.target.value })} />
                  <p className="form-hint">On your website, your emails and the browser tab.</p>
                </div>
                <div className="form-group">
                  <label htmlFor="teamCardCoaches" className="form-label">Team cards</label>
                  <select id="teamCardCoaches" className="form-input" value={formData.teamCardCoaches}
                    onChange={(e) => setFormData({ ...formData, teamCardCoaches: e.target.value as 'all' | 'lead' })}>
                    <option value="all">Full: every coach and the description</option>
                    <option value="lead">Compact: the lead coach only</option>
                  </select>
                  <p className="form-hint">How every team appears on your Teams page.</p>
                </div>
              </div>
              <div className="st-fields">
                <div className="st-actions">
                  {note('club')}
                  <button type="button" className="btn btn-primary" disabled={sectionSaving === 'club'}
                    onClick={() => saveSection('club', { siteName: formData.siteName })}>
                    {savingLabel('club', 'Save')}
                  </button>
                </div>
                <div className="st-actions">
                  {note('cards')}
                  <button type="button" className="btn btn-primary" disabled={sectionSaving === 'cards'}
                    onClick={() => saveSection('cards', { teamCardCoaches: formData.teamCardCoaches }, false)}>
                    {savingLabel('cards', 'Save', false)}
                  </button>
                </div>
              </div>
            </div>

            <div className="st-block">
              <h3>Colours</h3>
              {coloursError && <div className="error-message">{coloursError}</div>}
              {coloursSuccess && <div className="success-message">{coloursSuccess}</div>}
              <div className="st-colours">
                <div className="st-colour-pickers">
                  {([['Primary', primaryColor, setPrimaryColor], ['Secondary', secondaryColor, setSecondaryColor]] as const).map(([name, value, set]) => (
                    <div key={name} className="colour-row">
                      <label className="colour-row-label">{name}</label>
                      <div className="colour-row-controls">
                        <input type="color" className="colour-input-picker" value={value} onChange={(e) => set(e.target.value)} aria-label={`${name} colour picker`} />
                        <input type="text" className="form-input colour-input-hex" value={value} onChange={(e) => set(e.target.value.trim())} maxLength={7} aria-label={`${name} colour hex`} />
                      </div>
                      <p className="colour-hint" style={{ color: contrastBadge(value).color }}>
                        {HEX_RE.test(value) ? contrastBadge(value).label : 'Enter a valid hex'}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="colour-preview-card st-colour-preview">
                  <div className="colour-preview-band-primary" style={{ background: primaryColor }}>
                    <span style={{ color: contrastVsWhite(primaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a' }}>{formData.siteName || 'Your club'}</span>
                  </div>
                  <div className="colour-preview-band-secondary" style={{ background: secondaryColor }}>
                    <span style={{ color: contrastVsWhite(secondaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a' }}>Headlines</span>
                  </div>
                  <div className="colour-preview-buttons">
                    <button type="button" className="colour-preview-btn" style={{ background: primaryColor, color: contrastVsWhite(primaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a' }}>Button</button>
                    <button type="button" className="colour-preview-btn colour-preview-btn-outline"
                      style={{ color: contrastVsWhite(primaryColor) >= 3 ? primaryColor : '#555555', borderColor: contrastVsWhite(primaryColor) >= 3 ? primaryColor : '#555555' }}>Outline</button>
                  </div>
                </div>
              </div>
              <div className="st-actions">
                <button type="button" className="btn btn-secondary" onClick={handleResetColoursToBadge}
                  disabled={coloursSaving || coloursResetting || !hasBadge}
                  title={hasBadge ? 'Take the colours from your badge again' : 'Upload a badge first'}>
                  {coloursResetting ? 'Resetting…' : 'Use badge colours'}
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveColours} disabled={coloursSaving || coloursResetting}>
                  {coloursSaving ? 'Saving…' : saveButtonLabel('Save colours')}
                </button>
              </div>
            </div>

            {settings?.plan === 'pro' && (
              <div className="st-block">
                <h3>Website theme</h3>
                <p className="form-hint" style={{ marginTop: -4 }}>Pro. {previewMode ? 'Your pick saves to your draft.' : 'Changes as soon as you pick one.'}</p>
                {themeError && <div className="error-message">{themeError}</div>}
                {themeSuccess && <div className="success-message">{themeSuccess}</div>}
                <div className="theme-picker-grid st-theme-grid">
                  {([
                    [null, 'Classic', 'classic', 'Clean and content first. Works for any club.'],
                    ['matchday', 'Matchday', 'matchday', 'Bold and dark, with a big photo header.'],
                    ['newsfeed', 'Newsfeed', 'newsfeed', 'A lead story and a grid of news. For clubs that post often.'],
                    ['victory', 'Victory', 'victory', 'Club colours across the top, the lead story with the next match beside it.'],
                  ] as const).map(([value, name, cls, blurb]) => (
                    <button key={name} type="button" className={`theme-picker-card ${formData.themeName === value ? 'selected' : ''}`}
                      onClick={() => handleSaveTheme(value)} disabled={themeSaving}>
                      <div className={`theme-picker-preview theme-preview-${cls}`}><span className="theme-preview-label">{name}</span></div>
                      <div className="theme-picker-info"><h3>{name}</h3><p>{blurb}</p></div>
                      {formData.themeName === value && <div className="theme-picker-current">✓ Active</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Contact */}
          <section id="contact" className="st-group">
            <header className="st-group-head">
              <h2>Contact</h2>
              <p>Where messages from your website go.</p>
            </header>
            <div className="st-block">
              <div className="st-fields">
                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">Enquiry email</label>
                  <input id="contactEmail" type="email" className="form-input" value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} required />
                  <p className="form-hint">Join requests and contact forms come here, and to every admin with enquiry emails on under People and security.</p>
                </div>
                <div className="form-group">
                  <label htmlFor="contactPhone" className="form-label">Phone <span className="st-optional">optional</span></label>
                  <input id="contactPhone" type="tel" className="form-input" value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} />
                </div>
              </div>
              <div className="st-inline-row">
                <div>
                  <strong>Social links</strong>
                  <p className="form-hint" style={{ margin: '2px 0 0' }}>They live with your contact details on the website, in Pages.</p>
                </div>
                <a href="/content" className="btn btn-secondary" style={{ textDecoration: 'none' }}>Edit social links</a>
              </div>
              <div className="st-actions">
                {note('contact')}
                <button type="button" className="btn btn-primary" disabled={sectionSaving === 'contact'}
                  onClick={() => saveSection('contact', { contactEmail: formData.contactEmail, contactPhone: formData.contactPhone }, false)}>
                  {savingLabel('contact', 'Save', false)}
                </button>
              </div>
            </div>
          </section>

          {/* Plan and billing */}
          <section id="billing" className="st-group">
            <header className="st-group-head">
              <h2>Plan and billing</h2>
              <p>Your GrassrootsFC plan, card payments and analytics.</p>
            </header>

            <div className="st-block">
              <div className="st-inline-row">
                <div className="subscription-info-row" style={{ margin: 0 }}>
                  <div className="subscription-plan-display">
                    <span className="subscription-plan-label">Plan</span>
                    <span className={`subscription-plan-name ${subscription?.plan || plan}`}>
                      {(subscription?.plan || plan || 'free').charAt(0).toUpperCase() + (subscription?.plan || plan || 'free').slice(1)}
                    </span>
                  </div>
                  {subscription?.subscriptionStatus && (
                    <div className="subscription-plan-display">
                      <span className="subscription-plan-label">Status</span>
                      <span className={`status-badge ${subscription.subscriptionStatus === 'active' ? 'status-success' : subscription.subscriptionStatus === 'past_due' ? 'status-warning' : ''}`}>
                        {subscription.subscriptionStatus === 'active' ? 'Active' : subscription.subscriptionStatus === 'past_due' ? 'Past due' : subscription.subscriptionStatus}
                      </span>
                    </div>
                  )}
                  {subscription?.currentPeriodEnd && (
                    <div className="subscription-plan-display">
                      <span className="subscription-plan-label">Renews</span>
                      <span className="subscription-plan-value">{formatDate(subscription.currentPeriodEnd)}</span>
                    </div>
                  )}
                </div>
                {subscription?.hasSubscription ? (
                  <button type="button" className="btn btn-secondary" onClick={handleManageSubscription} disabled={isManagingSub}>
                    {isManagingSub ? 'Opening…' : 'Manage subscription'}
                  </button>
                ) : (
                  <a href="/upgrade" className="btn btn-primary" style={{ textDecoration: 'none' }}>Upgrade plan</a>
                )}
              </div>
            </div>

            <div className="st-block">
              <h3>Card payments</h3>
              <p className="form-hint" style={{ marginTop: -4 }}>
                For your shop and club fees, paid straight to the club's bank.{' '}
                {isFree ? '5% platform fee on Free, 1.5% on Club, 0.75% on Pro.' : plan === 'pro' ? '0.75% platform fee on Pro.' : '1.5% platform fee on Club.'}
              </p>
              {stripeError && <div className="error-message">{stripeError}</div>}
              {stripeMessage && <div className="success-message">{stripeMessage}</div>}
              <div className="stripe-status-area">
                {!stripeStatus ? (
                  <p className="form-hint">Checking your payments account…</p>
                ) : stripeStatus.connected && stripeStatus.chargesEnabled && !stripeStatus.clubPaysStripe ? (
                  <div className="stripe-connected">
                    <div className="status-badge status-success">Connected</div>
                    <p style={{ margin: '8px 0' }}>
                      Taking payments.{stripeStatus.payoutsEnabled ? ' Payouts to your bank are on.' : ' Payouts are being set up.'}
                    </p>
                    <button type="button" className="btn btn-secondary" onClick={handleViewStripeDashboard}>Manage on Stripe</button>
                  </div>
                ) : (
                  <ClubPaymentsScreens status={stripeStatus} onChange={loadStripeStatus} />
                )}
              </div>
            </div>

            <div className="st-block">
              <h3>Google Analytics</h3>
              {isFree ? (
                <div className="st-inline-row">
                  <p className="form-hint" style={{ margin: 0 }}>See who visits your website. Club and Pro.</p>
                  <a href="/upgrade" className="btn btn-secondary" style={{ textDecoration: 'none' }}>Upgrade</a>
                </div>
              ) : (
                <>
                  <div className="form-group" style={{ maxWidth: 360 }}>
                    <label htmlFor="gaTrackingId" className="form-label">Measurement ID</label>
                    <input id="gaTrackingId" type="text" className="form-input" value={formData.gaTrackingId}
                      onChange={(e) => setFormData({ ...formData, gaTrackingId: e.target.value })} placeholder="G-XXXXXXXXXX" />
                    <p className="form-hint">
                      In Google Analytics: Admin, Data streams, your website. It starts with G-.{' '}
                      <a href="https://support.google.com/analytics/answer/12270356" target="_blank" rel="noopener noreferrer">Where to find it</a>
                    </p>
                  </div>
                  <div className="st-actions">
                    {note('ga')}
                    <button type="button" className="btn btn-primary" disabled={sectionSaving === 'ga'}
                      onClick={() => saveSection('ga', { gaTrackingId: formData.gaTrackingId || undefined }, false)}>
                      {savingLabel('ga', 'Save', false)}
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* People and security */}
          <section id="people" className="st-group">
            <header className="st-group-head st-head-row">
              <div>
                <h2>People and security</h2>
                <p>
                  Who can log in to this dashboard.
                  {adminUsers && <span className="admin-limit-badge">{adminCount} of {adminLimit}</span>}
                </p>
              </div>
              {isOwner && (isFree || atAdminLimit ? (
                <a href="/upgrade" className="btn btn-secondary" style={{ textDecoration: 'none' }}>{isFree ? 'Upgrade to invite' : 'Upgrade for more'}</a>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => { setShowInviteModal(true); setAdminError(''); setAdminSuccess(''); }}>Invite admin</button>
              ))}
            </header>

            <div className="st-block">
              {adminError && <div className="error-message">{adminError}</div>}
              {adminSuccess && <div className="success-message">{adminSuccess}</div>}
              {isLoadingAdmins ? (
                <div className="admin-users-loading">Loading…</div>
              ) : (
                <div className="admin-users-list">
                  {(adminUsers?.admins || []).map((admin: AdminUser) => (
                    <div key={admin.email} className="admin-user-row">
                      <div className="admin-user-avatar">{(admin.name || admin.email).charAt(0).toUpperCase()}</div>
                      <div className="admin-user-info">
                        <div className="admin-user-name">
                          {admin.name || admin.email}
                          {admin.email === currentUserEmail && <span className="admin-you-badge">You</span>}
                        </div>
                        {admin.name && <div className="admin-user-email">{admin.email}</div>}
                        {admin.forcePasswordChange && <div className="admin-pending-badge">Password change pending</div>}
                      </div>
                      <label className="st-enquiry-toggle" title={isOwner || admin.email === currentUserEmail ? '' : 'Only the owner can change this'}>
                        <input type="checkbox" checked={admin.notifyInquiries !== false}
                          disabled={togglingEmail === admin.email || !(isOwner || admin.email === currentUserEmail)}
                          onChange={() => handleToggleEnquiries(admin)} />
                        <span>Enquiry emails</span>
                      </label>
                      <div className="admin-user-meta">
                        <span className={`admin-role-badge admin-role-${admin.role}`}>{admin.role === 'owner' ? 'Owner' : 'Admin'}</span>
                        {admin.createdAt && <span className="admin-user-date">Added {formatDateShort(admin.createdAt)}</span>}
                      </div>
                      {isOwner && admin.email !== currentUserEmail && admin.role !== 'owner' && (
                        <button type="button" className="admin-remove-btn" onClick={() => handleRemoveAdmin(admin.email)}
                          disabled={removingEmail === admin.email}>
                          {removingEmail === admin.email ? 'Removing…' : 'Remove'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form id="change-password-section" onSubmit={handleChangePassword} className="st-block">
              <h3>
                Change your password
                {forcePasswordChange && <span className="admin-required-badge">Required</span>}
              </h3>
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
              <div className="st-fields st-fields-3">
                <div className="form-group">
                  <label htmlFor="currentPassword" className="form-label">Current</label>
                  <input id="currentPassword" type="password" className="form-input" value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)} required autoComplete="current-password" />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">New</label>
                  <input id="newPassword" type="password" className="form-input" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">New, again</label>
                  <input id="confirmPassword" type="password" className="form-input" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
                </div>
              </div>
              <div className="st-actions">
                <span className="form-hint" style={{ margin: 0, marginRight: 'auto' }}>At least 8 characters.</span>
                <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
                  {isChangingPassword ? 'Changing…' : 'Change password'}
                </button>
              </div>
            </form>
          </section>

          {/* Danger zone */}
          <section id="danger" className="st-group st-group--danger">
            <header className="st-group-head">
              <h2>Danger zone</h2>
              <p>Things that take your website down, or take your data out.</p>
            </header>

            <div className="st-block st-inline-row">
              <label className="checkbox-label" style={{ margin: 0 }}>
                <input type="checkbox" checked={formData.maintenanceMode}
                  onChange={(e) => setFormData({ ...formData, maintenanceMode: e.target.checked })} />
                <span className="checkbox-text">Maintenance mode: show a holding message instead of your website</span>
              </label>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                {note('maintenance')}
                <button type="button" className="btn btn-secondary" disabled={sectionSaving === 'maintenance'}
                  onClick={() => saveSection('maintenance', { maintenanceMode: formData.maintenanceMode }, false)}>
                  {sectionSaving === 'maintenance' ? 'Saving…' : 'Save'}
                </button>
              </span>
            </div>

            {isOwnerFromJwt && (
              <>
                <div className="st-block st-inline-row">
                  <div>
                    <strong>Download your data</strong>
                    <p className="form-hint" style={{ margin: '2px 0 0' }}>Everything we hold for your club, as one JSON file.</p>
                  </div>
                  <button type="button" className="btn btn-outline" onClick={handleExportData} disabled={isExporting}>
                    {isExporting ? 'Preparing…' : 'Download'}
                  </button>
                </div>

                <div className="st-block">
                  {!deleteArmed ? (
                    <div className="st-inline-row">
                      <div>
                        <strong>Delete your club</strong>
                        <p className="form-hint" style={{ margin: '2px 0 0' }}>Your website, account and all your data, for good.</p>
                      </div>
                      <button type="button" className="btn btn-outline st-danger-btn" onClick={() => setDeleteArmed(true)}>Delete…</button>
                    </div>
                  ) : (
                    <div className="st-delete-confirm">
                      <p className="st-delete-title">This can't be undone.</p>
                      <p className="form-hint">
                        Your website goes offline at once, and your account, content, teams, players, members and images are deleted.
                        Download your data first. To confirm, type <strong>DELETE</strong>.
                      </p>
                      <input type="text" className="form-input" placeholder="Type DELETE" value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)} style={{ maxWidth: 260 }} />
                      <div className="st-actions" style={{ justifyContent: 'flex-start' }}>
                        <button type="button" className="btn btn-primary st-danger-fill" disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount} onClick={handleDeleteAccount}>
                          {isDeletingAccount ? 'Deleting…' : 'Delete my club for good'}
                        </button>
                        <button type="button" className="btn btn-outline" disabled={isDeletingAccount}
                          onClick={() => { setDeleteArmed(false); setDeleteConfirmText(''); setDangerError(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {dangerError && <p className="st-danger-text">{dangerError}</p>}
                </div>
              </>
            )}
          </section>
        </div>

        <nav className="st-nav" aria-label="Settings sections">
          <p className="st-nav-title">On this page</p>
          {groups.map(g => (
            <a key={g.id} href={`#${g.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(g.id)?.scrollIntoView({ behavior: 'smooth' }); }}>
              {g.label}
            </a>
          ))}
          <div className="st-nav-foot">
            <a href={`https://${clubId}.grassrootsfc.co.uk`} target="_blank" rel="noopener noreferrer">View your website</a>
            <span>Last saved {settings?.updatedAt ? formatDate(settings.updatedAt) : 'never'}</span>
            <span>Help: <a href="mailto:hello@grassrootsfc.co.uk">hello@grassrootsfc.co.uk</a></span>
          </div>
        </nav>
      </div>

      {showInviteModal && (
        <div className="admin-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">Invite an admin</h2>
              <button type="button" className="admin-modal-close" onClick={() => setShowInviteModal(false)} aria-label="Close">&times;</button>
            </div>
            <p className="form-hint" style={{ marginBottom: 20 }}>
              They'll get an email with a temporary password, and set their own when they first log in.
            </p>
            {adminError && <div className="error-message">{adminError}</div>}
            <form onSubmit={handleInviteAdmin}>
              <div className="form-group">
                <label htmlFor="inviteName" className="form-label">Name</label>
                <input id="inviteName" type="text" className="form-input" value={inviteName} onChange={(e) => setInviteName(e.target.value)} required placeholder="Sarah Smith" autoFocus />
              </div>
              <div className="form-group">
                <label htmlFor="inviteEmail" className="form-label">Email</label>
                <input id="inviteEmail" type="email" className="form-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required placeholder="sarah@yourclub.com" />
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isInviting}>{isInviting ? 'Sending…' : 'Send invite'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
